import React, { Component } from 'react';
import './listas-de-leitura.css';
import Sdk from 'api.digitalpages.module.sdk.api';
import Slider from "react-slick";

import {
  HeaderShelf,
  SidebarRoundedIcons,
  Footer,
  Preloader
} from '../../components';

export default class ViewListasDeLeitura extends Component {
  constructor(props) {
    super(props);
    this.sliderRefs = {};
    this.state = {
      loading: true,
      readingLists: [],
      showCreateModal: false,
      showImportModal: false,
      showShareModal: false,
      newListName: '',
      importCode: '',
      importedList: null,
      importError: '',
      importLoading: false,
      editingList: null,
      editingListName: '',
      shareList: null,
      shareCode: '',
      copySuccess: false,
      suggestedBooks: []
    };
  }

  componentDidMount() {
    this.loadReadingLists();
    this.checkForImportCode();
    this.loadSuggestedBooks();
  }

  checkForImportCode = () => {
    const urlParams = new URLSearchParams(window.location.hash.split('?')[1]);
    const importCode = urlParams.get('code');
    
    if (importCode) {
      this.setState({ 
        showImportModal: true,
        importCode: importCode
      }, () => {
        this.onImportNewList();
      });
    }
  }

  loadSuggestedBooks = async () => {
    try {
      const url = 'cms/v1.1/managed/content/available?order_consumption=desc&only_new_contents=true&page_size=6&include_properties=true&include_entities=true';
      const response = await Sdk.Api.dynamic.bridge(url, null, "GET");
      const suggestedBooks = response.result ? response.result.map(this.toContent) : [];
      this.setState({ suggestedBooks });
    } catch (error) {
      console.error('Error loading suggested books:', error);
    }
  }

  onAddBookToList = async (book, listUid) => {
    try {
      // Check if UserData already exists for this book
      const checkUrl = `cms/v1.1/user/data?context_type=ManagedContent&target_type=ManagedContent&target_uid=${book.uid}&context_uid=${book.uid}`;
      const existingResponse = await Sdk.Api.dynamic.bridge(checkUrl, null, 'GET');
      const existingData = existingResponse.result;
      
      if (existingData.length === 0) {
        // Create new UserData
        const userInfo = await Sdk.Api.dynamic.bridge('auth/v1.0/user/me', null, "GET");
        const userDataConnector = userInfo.directory?.connectors?.find(connector => 
          connector.features?.includes('IConnectorUserData')
        );
        
        if (!userDataConnector) {
          throw new Error('UserData connector not found');
        }

        const tagData = await this.getTagById(listUid);
        const createUrl = `cms/v1.1/user/connector/uid/${userDataConnector.uid}/data`;
        await Sdk.Api.dynamic.bridge(createUrl, {
          context_uid: book.uid,
          context_type: 'ManagedContent',
          target_uid: book.uid,
          target_type: 'ManagedContent',
          tags: [{ name: tagData.name }]
        }, 'POST');
      } else {
        // Update existing UserData
        const userData = existingData[0];
        const updateUrl = `cms/v1.1/user/data/uid/${userData.uid}`;
        
        if (!userData.tags) userData.tags = [];
        
        const tagExists = userData.tags.find(t => t.uid === listUid);
        if (!tagExists) {
          const tagData = await this.getTagById(listUid);
          userData.tags.push({ name: tagData.name });
          await Sdk.Api.dynamic.bridge(updateUrl, userData, 'PUT');
        }
      }
      
      // Update local state
      const updatedLists = this.state.readingLists.map(list => 
        list.uid === listUid 
          ? { ...list, books: [...list.books, book] }
          : list
      );
      this.setState({ readingLists: updatedLists });
    } catch (error) {
      console.error('Error adding book to list:', error);
    }
  }

  getTagById = async (tagUid) => {
    const tags = await this.getTags();
    return tags.find(tag => tag.uid === tagUid) || { name: 'Unknown Tag' };
  }

  loadReadingLists = async () => {
    try {
      // Get tags and user data
      const [tags, sharedTags, userData, userDataShared] = await Promise.all([
        this.getTags(),
        this.getSharedTags(),
        this.getUserData(),
        this.getUserDataShared()
      ]);
      
      // Filter userData for reading lists (exclude favorites)
      const data = [...userData, ...userDataShared].filter(p => 
        p.tags && 
        p.tags.length > 0 && 
        p.targetType === "ManagedContent" && 
        p.contextType === "ManagedContent" &&
        !p.tags.find(t => t.name === "###InternalFavorites###")
      );
      
      const uniqueTags = [...tags, ...sharedTags].filter(p => p.name !== "###InternalFavorites###");
      const contentsMap = {};
      
      // Group contents by tag
      for (const userDataItem of data) {
        for (const tag of userDataItem.tags || []) {
          let list = contentsMap[tag.uid];
          if (!list) {
            list = [];
            contentsMap[tag.uid] = list;
          }
          
          const contentData = await this.getContentDetail(userDataItem.contextUid);
          if (contentData) list.push(contentData);
        }
      }
      
      // Create reading lists with share status
      const readingLists = uniqueTags.map(tag => ({
        uid: tag.uid,
        title: tag.name,
        editable: true,
        isPublic: tag.shareEnabled || false,
        shareCode: tag.shareCode || '',
        books: contentsMap[tag.uid] || []
      }));
      
      this.setState({ 
        readingLists,
        loading: false 
      });
    } catch (error) {
      console.error('Error loading reading lists:', error);
      this.setState({ loading: false });
    }
  }

  getTags = async () => {
    const url = 'cms/v1.1/user/data/tags';
    const response = await Sdk.Api.dynamic.bridge(url, null, "GET");
    return response.map(this.toTagData);
  }

  getSharedTags = async () => {
    const url = 'cms/v1.1/user/data/tags/shared';
    const response = await Sdk.Api.dynamic.bridge(url, null, "GET");
    return response.filter(p => p.tag.active_share).map(p => this.toTagData(p.tag));
  }

  getUserData = async () => {
    const result = [];
    let page = 1;
    
    while (true) {
      const url = `cms/v1.1/user/data?page=${page}`;
      const response = await Sdk.Api.dynamic.bridge(url, null, "GET");
      
      result.push(...response.result.map(this.toUserData));
      
      if (!response.next_page_query) break;
      page++;
    }
    
    return result;
  }

  getUserDataShared = async () => {
    const result = [];
    let page = 1;
    
    while (true) {
      const url = `cms/v1.1/user/data/shared?page=${page}`;
      const response = await Sdk.Api.dynamic.bridge(url, null, "GET");
      
      result.push(...response.result.map(this.toUserData));
      
      if (!response.next_page_query) break;
      page++;
    }
    
    return result;
  }

  getContentDetail = async (contentUid) => {
    try {
      const url = `cms/v1.1/managed/content/group/uid/${contentUid}`;
      const response = await Sdk.Api.dynamic.bridge(url, null, "GET");
      return this.toContent(response);
    } catch (error) {
      console.error('Error loading content:', contentUid, error);
      return null;
    }
  }

  toTagData = (serverData) => ({
    uid: serverData.uid,
    name: serverData.name,
    shareCode: serverData.code_share,
    shareEnabled: serverData.active_share
  })

  toUserData = (serverData) => ({
    uid: serverData.uid,
    contextType: serverData.context_type,
    contextUid: serverData.context_uid,
    targetType: serverData.target_type,
    targetUid: serverData.target_uid,
    tags: serverData.tags?.map(t => ({ uid: t.uid, name: t.name }))
  })

  toContent = (serverData) => ({
    uid: serverData.uid,
    title: serverData.name || 'Título não disponível',
    // author: serverData.properties?.find(p => p.name === "Autor")?.value || 'Autor não disponível',
    description: serverData.properties?.find(p => p.name === "Descrição")?.value,
    image: this.getImageUrl(serverData.uid)
  })

  onBookClick = (book) => {
    window.location.hash = `#/book/${book.uid}`;
  }

  nextSlide = (listUid) => {
    if (this.sliderRefs[listUid]) {
      this.sliderRefs[listUid].slickNext();
    }
  }

  previousSlide = (listUid) => {
    if (this.sliderRefs[listUid]) {
      this.sliderRefs[listUid].slickPrev();
    }
  }

  getImageUrl(uid) {
      var url = `${Sdk.Api.domain}/cms/v1.1/managed/content/group/uid/${uid}/default/thumb?image_options=width:200&api_env=${Sdk.Api.enviroment}&project_key=${Sdk.Api.projectKey}&access_token=${Sdk.Api.authorization.credential.accessToken}`;
  
      return url;
    }

  onCreateList = () => {
    this.setState({ showCreateModal: true });
  }

  onImportList = () => {
    this.setState({ showImportModal: true });
  }

  onCloseModal = () => {
    this.setState({ 
      showCreateModal: false, 
      showImportModal: false,
      showShareModal: false,
      newListName: '',
      importCode: '',
      importedList: null,
      importError: '',
      importLoading: false,
      shareList: null,
      shareCode: '',
      copySuccess: false
    });
  }

  onCreateNewList = async () => {
    const { newListName } = this.state;
    if (newListName.trim()) {
      try {
        // Get user info with connectors
        const userInfo = await Sdk.Api.dynamic.bridge('auth/v1.0/user/me', null, "GET");
        // console.log('User info:', userInfo);
        
        // Find connector with IConnectorUserData feature
        const userDataConnector = userInfo.directory?.connectors?.find(connector => 
          connector.features?.includes('IConnectorUserData')
        );
        
        // console.log('UserData connector:', userDataConnector);
        
        if (!userDataConnector) {
          throw new Error('No connector with IConnectorUserData feature found');
        }
        
        // Create new tag
        const url = `cms/v1.1/user/connector/uid/${userDataConnector.uid}/tag`;
        const response = await Sdk.Api.dynamic.bridge(url, {
          name: newListName
        }, "POST");
        
        // Add new list to state
        const newList = {
          uid: response.uid,
          title: newListName,
          editable: true,
          isPublic: false,
          books: []
        };
        
        this.setState({
          readingLists: [...this.state.readingLists, newList]
        });
        
        this.onCloseModal();
      } catch (error) {
        console.error('Error creating list:', error);
      }
    }
  }

  onImportNewList = async () => {
    const { importCode, importLoading } = this.state;
    if (importCode.trim() && !importLoading) {
      this.setState({ importLoading: true, importError: '' });
      try {
        const tagData = await Sdk.Api.dynamic.bridge(`cms/v1.1/user/data/tags/shared/available?code_share=${importCode}`, null, 'GET');

        const url = `cms/v1.1/user/data/shared/available?code_share=${importCode}`;
        const response = await Sdk.Api.dynamic.bridge(url, null, "GET");
        
        if (response.result.length === 0) {
          this.setState({ importError: 'Código inválido ou lista não encontrada.', importLoading: false });
          return;
        }

        // Obter o nome da tag/lista
        const tagUid = tagData.result[0].uid;
        const tagName = tagData.result[0].name;
        
        // Carregar os conteúdos da lista
        const contents = await Promise.all(response.result.map(async (element) => {
          const contentData = await this.getContentDetail(element.context_uid);
          return contentData;
        }));
        
        // Filtrar conteúdos válidos
        const validContents = contents.filter(content => content !== null);
        
        this.setState({ 
          importedList: {
            uid: tagUid,
            title: tagName,
            books: validContents
          },
          importError: '',
          importLoading: false
        });
      } catch (error) {
        console.error('Error importing list:', error);
        this.setState({ 
          importError: 'Erro ao importar lista. Verifique o código e tente novamente.',
          importLoading: false
        });
      }
    }
  }

  onConfirmImport = async () => {
    const { importedList } = this.state;
    if (importedList) {
      try {
        // se o usuário confirmar a importação, clonar a lista para o usuário
        await Sdk.Api.dynamic.bridge(`cms/v1.1/user/data/tag/uid/${importedList.uid}/clone`, null, "POST");
        
        // Reload reading lists to show imported list
        await this.loadReadingLists();
        this.onCloseModal();
      } catch (error) {
        console.error('Error confirming import:', error);
        this.setState({ importError: 'Erro ao confirmar importação.' });
      }
    }
  }

  onEditList = (list, event) => {
    event.stopPropagation();
    this.setState({ 
      editingList: list.uid,
      editingListName: list.title 
    });
  }

  onSaveListName = async () => {
    const { editingListName, editingList } = this.state;
    try {
      // Update tag name via correct API endpoint
      const url = `cms/v1.1/user/tag/uid/${editingList}`;
      await Sdk.Api.dynamic.bridge(url, {
        name: editingListName
      }, "PUT");
      
      // Update local state
      const updatedLists = this.state.readingLists.map(list => 
        list.uid === editingList 
          ? { ...list, title: editingListName }
          : list
      );
      this.setState({ 
        readingLists: updatedLists,
        editingList: null,
        editingListName: ''
      });
    } catch (error) {
      console.error('Error updating list name:', error);
    }
  }

  onCancelEdit = () => {
    this.setState({ 
      editingList: null,
      editingListName: ''
    });
  }

  onDeleteList = async (listUid) => {
    try {
      // Delete tag via correct API endpoint
      const url = `cms/v1.1/user/tag/uid/${listUid}`;
      await Sdk.Api.dynamic.bridge(url, null, "DELETE");
      
      // Update local state
      const updatedLists = this.state.readingLists.filter(list => list.uid !== listUid);
      this.setState({ 
        readingLists: updatedLists,
        editingList: null,
        editingListName: ''
      });
    } catch (error) {
      console.error('Error deleting list:', error);
    }
  }

  onTogglePublic = async (listUid) => {
    const list = this.state.readingLists.find(l => l.uid === listUid);
    const newPublicState = !list.isPublic;
    
    try {
      // Update tag share status via correct API endpoint
      const url = `cms/v1.1/user/tag/uid/${listUid}`;
      const response = await Sdk.Api.dynamic.bridge(url, {
        active_share: newPublicState,
        name: list.name
      }, "PUT");
      
      // Update local state with new share code if enabled
      const updatedLists = this.state.readingLists.map(list => 
        list.uid === listUid 
          ? { 
              ...list, 
              isPublic: newPublicState,
              shareCode: newPublicState ? (response.code_share || list.shareCode) : ''
            }
          : list
      );
      this.setState({ readingLists: updatedLists });
    } catch (error) {
      console.error('Error updating list visibility:', error);
    }
  }

  onRemoveBookFromList = async (listUid, bookUid) => {
    try {
      // Find UserData entries for this book
      const checkUrl = `cms/v1.1/user/data?context_type=ManagedContent&target_type=ManagedContent&target_uid=${bookUid}&context_uid=${bookUid}`;
      const existingResponse = await Sdk.Api.dynamic.bridge(checkUrl, null, 'GET');
      const existingData = existingResponse.result;
      
      for (const userData of existingData) {
        if (!userData.tags) continue;
        
        // Check if this UserData has the tag we want to remove
        const hasTag = userData.tags.some(tag => tag.uid === listUid);
        if (hasTag) {
          // Remove the tag from the tags array
          userData.tags = userData.tags.filter(tag => tag.uid !== listUid);
          
          // Update the UserData
          const updateUrl = `cms/v1.1/user/data/uid/${userData.uid}`;
          await Sdk.Api.dynamic.bridge(updateUrl, userData, 'PUT');
        }
      }
      
      // Update local state
      const updatedLists = this.state.readingLists.map(list => 
        list.uid === listUid 
          ? { ...list, books: list.books.filter(book => book.uid !== bookUid) }
          : list
      );
      this.setState({ readingLists: updatedLists });
    } catch (error) {
      console.error('Error removing book from list:', error);
    }
  }

  onShareList = (list) => {
    if (list.isPublic && list.shareCode) {
      this.setState({ 
        showShareModal: true,
        shareList: list,
        shareCode: list.shareCode
      });
    }
  }

  onCopyShareCode = async () => {
    try {
      await navigator.clipboard.writeText(this.state.shareCode);
      this.setState({ copySuccess: true });
      setTimeout(() => {
        this.setState({ copySuccess: false });
      }, 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = this.state.shareCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.setState({ copySuccess: true });
      setTimeout(() => {
        this.setState({ copySuccess: false });
      }, 2000);
    }
  }

  onCopyShareUrl = async () => {
    const shareUrl = `${Sdk.Api.domain}/#/library/readinglist/uid/-1?code=${this.state.shareCode}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      this.setState({ copySuccess: true });
      setTimeout(() => {
        this.setState({ copySuccess: false });
      }, 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.setState({ copySuccess: true });
      setTimeout(() => {
        this.setState({ copySuccess: false });
      }, 2000);
    }
  }

  render() {
    const { 
      loading, 
      readingLists, 
      showCreateModal, 
      showImportModal, 
      showShareModal,
      newListName, 
      importCode,
      importedList,
      importError,
      importLoading,
      shareList,
      shareCode,
      copySuccess,
      editingList,
      suggestedBooks
    } = this.state;
    
    if (loading) {
      return (
        <Preloader/>
        // <div className="rdp-react-template-view-listas-de-leitura">
        //   <div className="rdp-react-template-view-listas-de-leitura-loading">
        //     <div className="rdp-react-template-view-listas-de-leitura-loader"></div>
        //   </div>
        // </div>
      );
    }

    return (
      <div className="rdp-react-template-view-listas-de-leitura">
        <div className="rdp-react-template-view-listas-de-leitura-header">
          <HeaderShelf
            onSignOut={async () => {
              const { UsersService } = await import('../../services/user');
              await UsersService.signOut();
              window.location = window.location.origin + window.location.pathname + "#/login";
            }}
            renderSearch={false}
          />
        </div>

        <div className="rdp-react-template-view-listas-de-leitura-sidebar">
          <SidebarRoundedIcons />
        </div>

        <div className="rdp-react-template-view-listas-de-leitura-main">
          <div className="rdp-react-template-view-listas-de-leitura-container">
            <div className="rdp-react-template-view-listas-de-leitura-header-section">
              <h1 className="rdp-react-template-view-listas-de-leitura-title">Listas de Leitura</h1>
              <div className="rdp-react-template-view-listas-de-leitura-actions">
                <button 
                  className="rdp-react-template-view-listas-de-leitura-import-btn"
                  onClick={this.onImportList}
                >
                  Importar Lista
                </button>
                <button 
                  className="rdp-react-template-view-listas-de-leitura-create-btn"
                  onClick={this.onCreateList}
                >
                  + Criar Lista
                </button>
              </div>
            </div>

            <div className="rdp-react-template-view-listas-de-leitura-content">
              {readingLists.length === 0 ? (
                <div className="rdp-react-template-view-listas-de-leitura-empty">
                  <p>Você ainda não possui <strong>Listas de Leitura</strong> criadas</p>
                </div>
              ) : (
                <div className="rdp-react-template-view-listas-de-leitura-lists">
                  {editingList ? (
                    // Show only editing list and suggestions
                    <>
                      {readingLists.filter(list => list.uid === editingList).map((list, index) => {
                        const sliderSettings = {
                          className: "slider variable-width",
                          dots: false,
                          infinite: false,
                          centerMode: false,
                          speed: 500,
                          slidesToShow: 5,
                          slidesToScroll: 5,
                          variableWidth: true,
                          afterChange: (current) => {
                            this.setState({ [`currentSlide_${list.uid}`]: current });
                          },
                          responsive: [
                            {
                              breakpoint: 767,
                              settings: {
                                slidesToShow: 2,
                                slidesToScroll: 2,
                                variableWidth: true
                              }
                            }
                          ],
                        };
                        
                        return (
                          <div key={list.uid || index} className="rdp-react-template-view-listas-de-leitura-list">
                            <div className="rdp-react-template-view-listas-de-leitura-list-header">
                              <div className="rdp-react-template-view-listas-de-leitura-list-header-left">
                                <div className="rdp-react-template-view-listas-de-leitura-edit-mode">
                                  <div className="rdp-react-template-view-listas-de-leitura-edit-row">
                                    <input
                                      type="text"
                                      value={this.state.editingListName}
                                      onChange={(e) => this.setState({ editingListName: e.target.value })}
                                      className="rdp-react-template-view-listas-de-leitura-edit-input"
                                    />
                                    <div className="rdp-react-template-view-listas-de-leitura-edit-actions">
                                      <button onClick={this.onSaveListName} className="rdp-react-template-view-listas-de-leitura-save-btn"></button>
                                      <button onClick={this.onCancelEdit} className="rdp-react-template-view-listas-de-leitura-cancel-btn"></button>
                                    </div>
                                  </div>
                                  <div className="rdp-react-template-view-listas-de-leitura-edit-options">
                                    <label className="rdp-react-template-view-listas-de-leitura-public-toggle">
                                      <input
                                        type="checkbox"
                                        checked={list.isPublic || false}
                                        onChange={() => this.onTogglePublic(list.uid)}
                                      />
                                      <span>Lista Pública</span>
                                    </label>
                                    {list.isPublic && list.shareCode && (
                                      <button 
                                        onClick={() => this.onShareList(list)} 
                                        className="rdp-react-template-view-listas-de-leitura-share-btn"
                                      >
                                        Compartilhar
                                      </button>
                                    )}
                                    <button onClick={() => this.onDeleteList(list.uid)} className="rdp-react-template-view-listas-de-leitura-delete-btn">Excluir Lista</button>
                                  </div>
                                </div>
                              </div>
                              <div className="rdp-react-template-view-listas-de-leitura-list-header-right">                            
                                <button 
                                  className={`rdp-react-template-view-listas-de-leitura-nav-btn ${(this.state[`currentSlide_${list.uid}`] || 0) <= 0 ? 'disabled' : 'enabled'}`}
                                  onClick={() => this.previousSlide(list.uid)}
                                  disabled={(this.state[`currentSlide_${list.uid}`] || 0) <= 0}
                                >
                                </button>
                                <button 
                                  className={`rdp-react-template-view-listas-de-leitura-nav-btn ${(this.state[`currentSlide_${list.uid}`] || 0) >= Math.max(0, (list.books?.length || 0) - 5) ? 'disabled' : 'enabled'}`}
                                  onClick={() => this.nextSlide(list.uid)}
                                  disabled={(this.state[`currentSlide_${list.uid}`] || 0) >= Math.max(0, (list.books?.length || 0) - 5)}
                                >
                                </button>
                              </div>
                            </div>
                            <div className="rdp-react-template-view-listas-de-leitura-slider-content">
                              <Slider 
                                ref={slider => this.sliderRefs[list.uid] = slider}
                                {...sliderSettings}
                              >
                                {list.books && list.books.map((book, bookIndex) => (
                                  <div 
                                    key={bookIndex} 
                                    className="rdp-react-template-view-listas-de-leitura-book"
                                    onClick={() => this.onBookClick(book)}
                                  >
                                    <button 
                                      className="rdp-react-template-view-listas-de-leitura-book-remove"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        this.onRemoveBookFromList(list.uid, book.uid);
                                      }}
                                    >✕</button>
                                    <img 
                                      className="rdp-react-template-view-listas-de-leitura-book-thumb blur"
                                      src={book.image} 
                                      alt={book.title}
                                      loading="lazy"
                                      onLoad={(e) => e.target.classList.add('loaded')}
                                    />
                                    <div className="rdp-react-template-view-listas-de-leitura-book-title">
                                      {book.title}
                                    </div>
                                    <div className="rdp-react-template-view-listas-de-leitura-book-author">
                                      {book.author}
                                    </div>
                                  </div>
                                ))}
                              </Slider>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Suggestions Section */}
                      {suggestedBooks.length > 0 && (
                        <div className="rdp-react-template-view-listas-de-leitura-list rdp-react-template-view-listas-de-leitura-suggestions">
                          <div className="rdp-react-template-view-listas-de-leitura-list-header">
                            <div className="rdp-react-template-view-listas-de-leitura-list-header-left">
                              <h3>Sugestões para sua lista</h3>
                            </div>
                            <div className="rdp-react-template-view-listas-de-leitura-list-header-right">
                              <button 
                                className={`rdp-react-template-view-listas-de-leitura-nav-btn ${(this.state.currentSlide_suggestions || 0) <= 0 ? 'disabled' : 'enabled'}`}
                                onClick={() => this.previousSlide('suggestions')}
                                disabled={(this.state.currentSlide_suggestions || 0) <= 0}
                              >
                              </button>
                              <button 
                                className={`rdp-react-template-view-listas-de-leitura-nav-btn ${(this.state.currentSlide_suggestions || 0) >= Math.max(0, suggestedBooks.filter(book => {
                                  const editingListData = readingLists.find(l => l.uid === editingList);
                                  return !editingListData?.books?.some(listBook => listBook.uid === book.uid);
                                }).length - 5) ? 'disabled' : 'enabled'}`}
                                onClick={() => this.nextSlide('suggestions')}
                                disabled={(this.state.currentSlide_suggestions || 0) >= Math.max(0, suggestedBooks.filter(book => {
                                  const editingListData = readingLists.find(l => l.uid === editingList);
                                  return !editingListData?.books?.some(listBook => listBook.uid === book.uid);
                                }).length - 5)}
                              >
                              </button>
                            </div>
                          </div>
                          <div className="rdp-react-template-view-listas-de-leitura-slider-content">
                            <Slider 
                              ref={slider => this.sliderRefs['suggestions'] = slider}
                              className="slider variable-width"
                              dots={false}
                              infinite={false}
                              centerMode={false}
                              speed={500}
                              slidesToShow={5}
                              slidesToScroll={5}
                              variableWidth={true}
                              afterChange={(current) => {
                                this.setState({ currentSlide_suggestions: current });
                              }}
                              responsive={[
                                {
                                  breakpoint: 767,
                                  settings: {
                                    slidesToShow: 2,
                                    slidesToScroll: 2,
                                    variableWidth: true
                                  }
                                }
                              ]}
                            >
                              {suggestedBooks
                                .filter(book => {
                                  const editingListData = readingLists.find(l => l.uid === editingList);
                                  return !editingListData?.books?.some(listBook => listBook.uid === book.uid);
                                })
                                .map((book, bookIndex) => (
                                <div 
                                  key={bookIndex} 
                                  className="rdp-react-template-view-listas-de-leitura-book rdp-react-template-view-listas-de-leitura-suggestion-book"
                                  onClick={() => this.onBookClick(book)}
                                >
                                  <button 
                                    className="rdp-react-template-view-listas-de-leitura-book-add"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      this.onAddBookToList(book, editingList);
                                    }}
                                  >+</button>
                                  <img 
                                    className="rdp-react-template-view-listas-de-leitura-book-thumb blur"
                                    src={book.image} 
                                    alt={book.title}
                                    loading="lazy"
                                    onLoad={(e) => e.target.classList.add('loaded')}
                                  />
                                  <div className="rdp-react-template-view-listas-de-leitura-book-title">
                                    {book.title}
                                  </div>
                                  <div className="rdp-react-template-view-listas-de-leitura-book-author">
                                    {book.author}
                                  </div>
                                </div>
                              ))}
                            </Slider>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    // Show all lists normally
                    readingLists.map((list, index) => {
                      const sliderSettings = {
                        className: "slider variable-width",
                        dots: false,
                        infinite: false,
                        centerMode: false,
                        speed: 500,
                        slidesToShow: 5,
                        slidesToScroll: 5,
                        variableWidth: true,
                        afterChange: (current) => {
                          this.setState({ [`currentSlide_${list.uid}`]: current });
                        },
                        responsive: [
                          {
                            breakpoint: 767,
                            settings: {
                              slidesToShow: 2,
                              slidesToScroll: 2,
                              variableWidth: true
                            }
                          }
                        ],
                      };
                      
                      return (
                        <div key={list.uid || index} className="rdp-react-template-view-listas-de-leitura-list">
                          <div className="rdp-react-template-view-listas-de-leitura-list-header">
                            <div className="rdp-react-template-view-listas-de-leitura-list-header-left">
                              <h3>{list.title}</h3>
                              <button 
                                className="rdp-react-template-view-listas-de-leitura-edit-btn"
                                onClick={(e) => this.onEditList(list, e)}
                              >
                              </button>
                            </div>
                            <div className="rdp-react-template-view-listas-de-leitura-list-header-right">                            
                              <button 
                                className={`rdp-react-template-view-listas-de-leitura-nav-btn ${(this.state[`currentSlide_${list.uid}`] || 0) <= 0 ? 'disabled' : 'enabled'}`}
                                onClick={() => this.previousSlide(list.uid)}
                                disabled={(this.state[`currentSlide_${list.uid}`] || 0) <= 0}
                              >
                              </button>
                              <button 
                                className={`rdp-react-template-view-listas-de-leitura-nav-btn ${(this.state[`currentSlide_${list.uid}`] || 0) >= Math.max(0, (list.books?.length || 0) - 5) ? 'disabled' : 'enabled'}`}
                                onClick={() => this.nextSlide(list.uid)}
                                disabled={(this.state[`currentSlide_${list.uid}`] || 0) >= Math.max(0, (list.books?.length || 0) - 5)}
                              >
                              </button>
                            </div>
                          </div>
                          <div className="rdp-react-template-view-listas-de-leitura-slider-content">
                            <Slider 
                              ref={slider => this.sliderRefs[list.uid] = slider}
                              {...sliderSettings}
                            >
                              {list.books && list.books.map((book, bookIndex) => (
                                <div 
                                  key={bookIndex} 
                                  className="rdp-react-template-view-listas-de-leitura-book"
                                  onClick={() => this.onBookClick(book)}
                                >
                                  <img 
                                    className="rdp-react-template-view-listas-de-leitura-book-thumb blur"
                                    src={book.image} 
                                    alt={book.title}
                                    loading="lazy"
                                    onLoad={(e) => e.target.classList.add('loaded')}
                                  />
                                  <div className="rdp-react-template-view-listas-de-leitura-book-title">
                                    {book.title}
                                  </div>
                                  <div className="rdp-react-template-view-listas-de-leitura-book-author">
                                    {book.author}
                                  </div>
                                </div>
                              ))}
                            </Slider>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
            <Footer/>
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="rdp-react-template-view-listas-de-leitura-modal">
            <div className="rdp-react-template-view-listas-de-leitura-modal-content">
              <h3>Criar Nova Lista</h3>
              <input
                type="text"
                placeholder="Nome da lista"
                value={newListName}
                onChange={(e) => this.setState({ newListName: e.target.value })}
                className="rdp-react-template-view-listas-de-leitura-modal-input"
              />
              <div className="rdp-react-template-view-listas-de-leitura-modal-actions">
                <button onClick={this.onCloseModal}>Cancelar</button>
                <button onClick={this.onCreateNewList}>Criar</button>
              </div>
            </div>
          </div>
        )}

        {/* Import Modal */}
        {showImportModal && (
          <div className="rdp-react-template-view-listas-de-leitura-modal">
            <div className="rdp-react-template-view-listas-de-leitura-modal-content rdp-react-template-view-listas-de-leitura-import-modal">
              <h3>Importar Lista</h3>
              <input
                type="text"
                placeholder="Código da lista"
                value={importCode}
                onChange={(e) => this.setState({ importCode: e.target.value, importedList: null, importError: '' })}
                className="rdp-react-template-view-listas-de-leitura-modal-input"
              />
              
              {importLoading && (
                <div className="rdp-react-template-view-listas-de-leitura-import-loading">
                  Carregando lista...
                </div>
              )}
              
              {importError && (
                <div className="rdp-react-template-view-listas-de-leitura-import-error">
                  {importError}
                </div>
              )}
              
              {importedList && (
                <div className="rdp-react-template-view-listas-de-leitura-import-preview">
                  <div className="rdp-react-template-view-listas-de-leitura-import-list-info">
                    <h4>{importedList.title}</h4>
                    {importedList.books && importedList.books.length > 0 && (
                      <div className="rdp-react-template-view-listas-de-leitura-import-books">
                        {importedList.books.slice(0, 3).map((book, index) => (
                          <div key={index} className="rdp-react-template-view-listas-de-leitura-import-book">
                            <img 
                              src={book.image} 
                              alt={book.title}
                              className="rdp-react-template-view-listas-de-leitura-import-book-thumb"
                              style={{ width: '60px', height: '80px', objectFit: 'cover', margin: '5px' }}
                            />
                          </div>
                        ))}
                        {importedList.books.length > 3 && (
                          <div className="rdp-react-template-view-listas-de-leitura-import-more">
                            +{importedList.books.length - 3} mais
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="rdp-react-template-view-listas-de-leitura-modal-actions">
                <button onClick={this.onCloseModal}>Cancelar</button>
                {!importedList ? (
                  <button onClick={this.onImportNewList} disabled={importLoading}>Verificar</button>
                ) : (
                  <button onClick={this.onConfirmImport}>Confirmar Importação</button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Share Modal */}
        {showShareModal && shareList && (
          <div className="rdp-react-template-view-listas-de-leitura-modal">
            <div className="rdp-react-template-view-listas-de-leitura-modal-content rdp-react-template-view-listas-de-leitura-share-modal">
              <h3>Compartilhar Lista: {shareList.title}</h3>
              <div className="rdp-react-template-view-listas-de-leitura-share-section">
                <label>Código de Compartilhamento:</label>
                <div className="rdp-react-template-view-listas-de-leitura-share-code-container">
                  <input
                    type="text"
                    value={shareCode}
                    readOnly
                    className="rdp-react-template-view-listas-de-leitura-share-code-input"
                  />
                  <button 
                    onClick={this.onCopyShareCode}
                    className="rdp-react-template-view-listas-de-leitura-copy-btn"
                  >
                    {copySuccess ? '✓' : 'Copiar'}
                  </button>
                </div>
              </div>
              <div className="rdp-react-template-view-listas-de-leitura-share-section">
                <label>Link de Compartilhamento:</label>
                <div className="rdp-react-template-view-listas-de-leitura-share-url-container">
                  <input
                    type="text"
                    value={`${window.location.origin}/#/library/readinglist/uid/-1?code=${this.state.shareCode}`}
                    readOnly
                    className="rdp-react-template-view-listas-de-leitura-share-url-input"
                  />
                  <button 
                    onClick={this.onCopyShareUrl}
                    className="rdp-react-template-view-listas-de-leitura-copy-btn"
                  >
                    {copySuccess ? '✓' : 'Copiar'}
                  </button>
                </div>
              </div>
              <div className="rdp-react-template-view-listas-de-leitura-modal-actions">
                <button onClick={this.onCloseModal}>Fechar</button>
              </div>
            </div>
          </div>
        )}
        
      </div>
    );
  }
}